/**
 * ColumnGraph component module.
 * @module shared/components/ColumnGraph
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {formatter} from '../../utils/utils';
import {withTheme} from 'styled-components/macro';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';

// ColumnGraph graph max size of tile
const ColumnGraph = ({
  height = '100%',
  width = '100%',
  data,
  hideLegend,
  action,
  theme,
}) => {
  const options = React.useMemo(() => ({
    title: {
      text: '',
    },
    useHighStocks: false,
    series: data,
    rangeSelector: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    legend: hideLegend ? {enabled: false} : {
      itemHoverStyle: {
        color: '#00D0BE',
      },
      align: 'left',
      verticalAlign: 'middle',
      layout: 'vertical',
      x: 0,
      y: 0,
      itemStyle: {color: '#ddd'},
    },
    credits: {enabled: false},
    colors: ['#00D0BE'],
    chart: {
      type: 'column',
      //panning: false,
      backgroundColor: 'rgba(0,0,0,0.00)',
      plotBorderWidth: 0,
      borderRadius: 0,
      //spacingRight:30,
      plotShadow: false,
      zoomType: '',
      pinchType: '',
    },
    xAxis: {
      type: 'category',
      opposite: false,
      gridLineWidth: 0,
      tickLength: 0,
      lineWidth: 0,
      minorGridLineWidth: 0,
      lineColor: 'transparent',
      labels: {
        align: 'center',
        format: '{value}',
        style: {
          fontFamily: theme.bodyText.name,
          fontSize: '14px',
          color: theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
        },
      },
    },
    yAxis: {
      title: {
        text: false,
      },
      labels: {
        enabled: false,
        align: 'center',
      },
      gridLineWidth: 0,
      opposite: false,
      plotLines: [
        {
          color: '#FFf',
          width: 1,
          value: 0,
        },
      ],
    },
    plotOptions: {
      series: {
        cursor: 'pointer',
        point: {
          events: {
            click: action ? p => action(p.point) : null,
          },
        },
      },
      column: {
        borderRadius: 0,
        color: theme.neumorphic ? theme.primaryColor : undefined,
        borderWidth: theme.neumorphic ? 1 : 0,
        shadow: theme.neumorphic ? {color: '#000000', offsetX: 0, offsetY: 0, opacity: 0.2, width: 6} : false,
        borderColor: theme.neumorphic ? {
          linearGradient: {x1: 0, x2: 1, y1: 1, y2: 1},
          stops: [
            [0, Highcharts.Color('#FFFFFF').setOpacity(0.4).get('rgba')],
            [1, Highcharts.Color('#000000').setOpacity(0.3).get('rgba')],
          ],
        } : undefined,
        dataLabels: {
          enabled: true,
          style: {
            fontFamily: theme.bodyText.name,
            fontSize: '14px',
            color: theme.dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.9)',
            fontWeight: 'normal',
            textOutline: 'none',
          },
          formatter: function performancePlotFormatter() {
            return formatter('number', {decimalPlaces: 1})(this.y) + '%';
          },
          //rotation: -90,
          align: 'center',
          //x: 4,
          //y: -4
        },
      },
    },
    tooltip: {
      enabled: false,
    },
  }), [data]);

  return <HighchartsReact containerProps={{style: {height, width}}}
                          highcharts={Highcharts}
                          options={options}/>;
};

export default withTheme(ColumnGraph);

ColumnGraph.propTypes = {
  height: PropTypes.string,
  width: PropTypes.string,
  title: PropTypes.string,
  hideLegend: PropTypes.bool,
  action: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.shape({})),
  theme: PropTypes.object,
};
